import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import SyncChip from "@/components/SyncChip";
import FieldHomeLink from "@/components/FieldHomeLink";
import { CheckCircle, SignIn, SignOut, LockKey, MapPin, HardHat, UserCircle, CloudArrowUp } from "@phosphor-icons/react";
import OfflineIndicator from "@/components/OfflineIndicator";
import StaleDataBanner from "@/components/StaleDataBanner";
import InstallPrompt from "@/components/InstallPrompt";
import { queueSubmission } from "@/lib/outbox";
import { cachePut, cacheGet } from "@/lib/refcache";

const inp = "w-full h-[54px] px-[15px] border-[1.5px] border-border-control bg-white text-[16px] rounded-[12px] focus:border-ink-primary focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)] disabled:bg-surface-inset disabled:text-ink-disabled";
const lbl = "text-xs font-bold uppercase tracking-wider text-gray-500 mb-1 block";

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

async function getPosition() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  });
}

const readWorker = () => {
  try { return JSON.parse(localStorage.getItem("eq_field_worker") || "null"); } catch { return null; }
};

export default function WorkerSignIn() {
  const [params] = useSearchParams();
  const projectId = params.get("project");

  const savedWorker = readWorker();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [manual, setManual] = useState(!savedWorker);
  const [mode, setMode] = useState("worker"); // worker | visitor
  const [sitePin, setSitePin] = useState("");
  const [workerPin, setWorkerPin] = useState("");
  const [visitorName, setVisitorName] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [staleAt, setStaleAt] = useState(null);

  useEffect(() => {
    if (!projectId) { setError("No project selected. Scan the site's Sign-In QR code to begin."); return; }
    api.get(`/public/project-signin/${projectId}`)
      .then(({ data }) => {
        setInfo(data);
        cachePut(`signin:${projectId}`, data, null).catch(() => {});
      })
      .catch(async (e) => {
        if (!e.response) {
          const hit = await cacheGet(`signin:${projectId}`).catch(() => null);
          if (hit) { setInfo(hit.data); setStaleAt(hit.cached_at); return; }
          setError("You're offline and this site hasn't been opened on this device before. Connect once to enable offline use.");
        } else {
          setError(formatApiErrorDetail(e.response?.data?.detail) || "This sign-in link is invalid.");
        }
      });
  }, [projectId]);

  const submit = async () => {
    setError("");
    if (!sitePin.trim()) { setError("Enter the Site PIN displayed on site."); return; }
    if (mode === "worker" && !workerPin.trim()) { setError("Enter your personal Worker PIN."); return; }
    if (mode === "visitor" && !visitorName.trim()) { setError("Enter your name."); return; }
    setSubmitting(true);
    try {
      let coords = {};
      if (info?.geofence) {
        const pos = await getPosition();
        if (pos) coords = pos;
      }
      const body = {
        site_pin: sitePin.trim(),
        worker_pin: mode === "worker" ? workerPin.trim() : "",
        visitor_name: mode === "visitor" ? visitorName.trim() : "",
        company: mode === "visitor" ? company.trim() : "",
        client_ref: crypto.randomUUID(),
        captured_at: new Date().toISOString(),
        ...coords,
      };
      const { data } = await api.post(`/public/project-signin/${projectId}`, body);
      if (data.field_token && data.worker_id) {
        localStorage.setItem("eq_field_worker", JSON.stringify({ id: data.worker_id, name: data.name, token: data.field_token }));
      }
      setResult(data);
    } catch (e) {
      if (!e.response) {
        const name = mode === "worker" ? "your entry" : visitorName.trim();
        await queueSubmission("signin", `/public/project-signin/${projectId}`, {
          site_pin: sitePin.trim(),
          worker_pin: mode === "worker" ? workerPin.trim() : "",
          visitor_name: mode === "visitor" ? visitorName.trim() : "",
          company: mode === "visitor" ? company.trim() : "",
          client_ref: crypto.randomUUID(),
          captured_at: new Date().toISOString(),
        });
        setResult({ action: "queued", name });
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.");
      }
    }
    setSubmitting(false);
  };

  const submitQuick = async () => {
    setError(""); setSubmitting(true);
    try {
      let coords = {};
      if (info?.geofence) { const pos = await getPosition(); if (pos) coords = pos; }
      const body = {
        worker_id: savedWorker.id, field_token: savedWorker.token,
        client_ref: crypto.randomUUID(), captured_at: new Date().toISOString(), ...coords,
      };
      const { data } = await api.post(`/public/project-signin/${projectId}`, body);
      setResult(data);
    } catch (e) {
      if (!e.response) {
        await queueSubmission("signin", `/public/project-signin/${projectId}`, {
          worker_id: savedWorker.id, field_token: savedWorker.token,
          client_ref: crypto.randomUUID(), captured_at: new Date().toISOString(),
        });
        setResult({ action: "queued", name: savedWorker.name });
      } else if (e.response.status === 403) {
        // Not rostered here → fall back to the full Site PIN flow.
        setManual(true);
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Enter the Site PIN to sign in here.");
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.");
      }
    }
    setSubmitting(false);
  };

  const reset = () => {
    setResult(null); setWorkerPin(""); setVisitorName(""); setCompany(""); setSitePin(""); setError("");
    setManual(!savedWorker);
  };

  // Identified workers: after confirmation, return to the Home screen automatically.
  useEffect(() => {
    if (result && (result.action === "signed_in" || result.action === "signed_out" || result.action === "queued") && savedWorker) {
      const t = setTimeout(() => navigate("/field"), 2600);
      return () => clearTimeout(t);
    }
  }, [result, savedWorker, navigate]);

  return (
    <div className="min-h-screen bg-surface-page flex flex-col" data-testid="signin-page">
      <InstallPrompt />
      <OfflineIndicator />
      <StaleDataBanner cachedAt={staleAt} />
      <header className="h-16 bg-equipr-black flex items-center px-5 shrink-0">
        <BrandLogo variant="dark" />
        <span className="ml-auto flex items-center gap-3"><FieldHomeLink variant="dark" /><SyncChip /><span className="text-[11px] font-bold uppercase tracking-[0.2em] text-equipr-yellow">Worker Sign-In</span></span>
      </header>

      <main className="flex-1 flex items-start justify-center p-4">
        <div className="w-full max-w-md mt-4">
          {info && (
            <div className="mb-4 text-center">
              <p className="text-xs uppercase tracking-wider text-gray-400">You are signing in at</p>
              <h1 className="text-xl font-extrabold text-equipr-black" data-testid="signin-project-name">{info.project_name}</h1>
              {info.geofence && (
                <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5">
                  <MapPin size={12} weight="fill" /> Location check enabled — you must be on site
                </p>
              )}
            </div>
          )}

          {result ? (
            result.action === "queued" ? (
              <div className="bg-white border border-gray-200 shadow-sm p-6 text-center" data-testid="signin-queued">
                <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <CloudArrowUp size={30} weight="bold" />
                </div>
                <h2 className="mt-4 text-lg font-extrabold text-equipr-black">Saved on this device</h2>
                <p className="text-sm text-gray-600 mt-1">No coverage right now — your sign-in/out will send automatically when back in signal.</p>
                <button onClick={() => (savedWorker ? navigate("/field") : reset())} data-testid="signin-queued-done"
                  className="mt-5 h-11 w-full bg-equipr-yellow text-equipr-black font-bold rounded-none hover:bg-equipr-yellow-dark">
                  {savedWorker ? "Back to Home" : "Done"}
                </button>
              </div>
            ) : (
            <div className="bg-white border border-gray-200 shadow-sm p-6 text-center" data-testid="signin-result">
              <div className={`mx-auto w-14 h-14 flex items-center justify-center rounded-full ${result.action === "signed_in" ? "bg-green-100 text-green-600" : "bg-blue-100 text-blue-600"}`}>
                {result.action === "signed_in" ? <SignIn size={30} weight="bold" /> : <SignOut size={30} weight="bold" />}
              </div>
              <h2 className="mt-4 text-lg font-extrabold text-equipr-black">
                {result.action === "signed_in" ? "Signed in" : "Signed out"}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                <b>{result.name}</b>{result.action === "signed_in"
                  ? ` — welcome. Signed in at ${fmtTime(result.sign_in_at)}.`
                  : ` — signed out at ${fmtTime(result.sign_out_at)}.`}
              </p>
              {result.action === "signed_out" && result.hours != null && (
                <p className="mt-2 inline-block bg-equipr-black text-white text-sm font-bold px-3 py-1">
                  Time on site: {result.hours} h
                </p>
              )}
              <p className="text-[12px] text-gray-400 mt-3">Scan the QR again when you {result.action === "signed_in" ? "leave" : "arrive"} to sign {result.action === "signed_in" ? "out" : "in"}.</p>
              {savedWorker ? (
                <>
                  <button onClick={() => navigate("/field")} data-testid="signin-home"
                    className="mt-5 h-11 w-full bg-equipr-yellow text-equipr-black font-bold rounded-none hover:bg-equipr-yellow-dark">
                    Back to Home
                  </button>
                  <p className="text-[11px] text-gray-400 mt-2">Taking you home…</p>
                </>
              ) : (
                <button onClick={reset} data-testid="signin-again"
                  className="mt-5 h-11 w-full bg-equipr-yellow text-equipr-black font-bold rounded-none hover:bg-equipr-yellow-dark">
                  Done
                </button>
              )}
            </div>
            )
          ) : (savedWorker && !manual) ? (
            <div className="bg-white border border-gray-200 shadow-sm p-6 text-center" data-testid="signin-quick">
              {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 mb-3 text-left" data-testid="signin-error">{error}</div>}
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center justify-center gap-1.5">
                <HardHat size={14} weight="bold" /> G'day {String(savedWorker.name || "").split(" ")[0]}
              </p>
              <p className="text-sm text-gray-600 mt-2">You're rostered here — one tap to sign in or out. If you're already on site, this signs you out.</p>
              <button onClick={submitQuick} disabled={submitting || !info} data-testid="signin-quick-submit"
                className="mt-4 h-12 w-full bg-equipr-yellow text-equipr-black font-extrabold text-lg rounded-none hover:bg-equipr-yellow-dark disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting ? "Please wait…" : <><SignIn size={18} weight="bold" /> Sign In / Out</>}
              </button>
              <button onClick={() => { setManual(true); setError(""); }} data-testid="signin-use-pin"
                className="mt-3 text-[12px] text-gray-500 hover:text-equipr-black underline">
                Signing in somewhere else? Use the Site PIN
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 shadow-sm p-5 space-y-4">
              {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="signin-error">{error}</div>}

              {savedWorker && (
                <button onClick={() => { setManual(false); setError(""); }} data-testid="signin-back-quick"
                  className="text-[12px] text-gray-500 hover:text-equipr-black underline">← Back to one-tap sign-in</button>
              )}

              <div>
                <label className={lbl}><LockKey size={12} className="inline mr-1" />Site PIN</label>
                <input value={sitePin} onChange={(e) => setSitePin(e.target.value)} inputMode="numeric"
                  placeholder="Shown on the sign-in board" className={inp + " tracking-[0.3em] font-bold text-center"}
                  data-testid="signin-site-pin" />
              </div>

              <div className="flex border border-gray-300">
                <button onClick={() => setMode("worker")} data-testid="signin-mode-worker"
                  className={`flex-1 h-10 text-sm font-bold flex items-center justify-center gap-1.5 ${mode === "worker" ? "bg-equipr-black text-white" : "bg-white text-gray-600"}`}>
                  <HardHat size={16} weight="bold" /> I'm a worker
                </button>
                <button onClick={() => setMode("visitor")} data-testid="signin-mode-visitor"
                  className={`flex-1 h-10 text-sm font-bold flex items-center justify-center gap-1.5 ${mode === "visitor" ? "bg-equipr-black text-white" : "bg-white text-gray-600"}`}>
                  <UserCircle size={16} weight="bold" /> Visitor
                </button>
              </div>

              {mode === "worker" ? (
                <div>
                  <label className={lbl}>Your Worker PIN</label>
                  <input value={workerPin} onChange={(e) => setWorkerPin(e.target.value)} inputMode="numeric"
                    placeholder="Your personal PIN" className={inp + " tracking-[0.3em] font-bold text-center"}
                    data-testid="signin-worker-pin" />
                  <p className="text-[11px] text-gray-400 mt-1">Don't know your PIN? Ask the site foreman.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className={lbl}>Full name</label>
                    <input value={visitorName} onChange={(e) => setVisitorName(e.target.value)}
                      placeholder="Your name" className={inp} data-testid="signin-visitor-name" />
                  </div>
                  <div>
                    <label className={lbl}>Company (optional)</label>
                    <input value={company} onChange={(e) => setCompany(e.target.value)}
                      placeholder="Your employer" className={inp} data-testid="signin-company" />
                  </div>
                </div>
              )}

              <button onClick={submit} disabled={submitting || !info} data-testid="signin-submit"
                className="h-12 w-full bg-equipr-yellow text-equipr-black font-extrabold text-lg rounded-none hover:bg-equipr-yellow-dark disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting ? "Please wait…" : <>Sign In / Out</>}
              </button>
              <p className="text-center text-[11px] text-gray-400">If you're already on site, this signs you out.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
